import { CheckIcon } from '@heroicons/react/20/solid';
import {
  DocumentTextIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';

import { TemplatePreview } from '@/components/templates-v2/document-generation/templates/TemplatePreview';
import { Button } from '@/components/ui/catalyst/button';
import { Dialog, DialogTitle } from '@/components/ui/catalyst/dialog';
import { Text } from '@/components/ui/catalyst/text';
import { Z_INDEX_MODAL_DEEPLY_NESTED } from '@/components/ui/zIndex';
import { cn } from '@/lib/utils';
import { enhancedApi as adminTemplatesApi } from '@/services/api/generated/adminTemplatesService';
import type {
  ReadTemplateSummary,
  TemplateCategory as TemplateCategoryType,
  TemplateFormat,
} from '@/services/api/generated/templatesV2Service';
import { useTemplateListQuery } from '@/services/api/generated/templatesV2Service';
import { type PackageRequestInfo } from '@/services/api/hipaaPackageService';
import { useGetMatterDetailsQuery } from '@/services/api/mattersService';

// Display item normalized from firm or global API
interface TemplateDisplayItem {
  id: string;
  name: string;
  display_name: string | null;
  description?: string | null;
  category: string;
  is_default: boolean;
  is_firm_template: boolean;
  format: TemplateFormat;
}

interface TemplateSearchListProps {
  requestInfo: PackageRequestInfo;
  onSelectTemplate: (templateId: string) => void;
  isLoading?: boolean;
  includeCategories?: TemplateCategoryType[];
  allowedTemplateTypes?: TemplateFormat[];
  enablePreview?: boolean;
  autoConfirmOnSelect?: boolean;
  confirmButtonLabel?: string;
}

// Template categories for better organization
const TemplateCategories = {
  AUTHORIZATION: 'HIPAA_AUTHORIZATION',
  COVERSHEET: 'HIPAA_COVERSHEET',
  RETAINER: 'RETAINER',
  OTHER: 'OTHER',
} as const;

type TemplateCategoryKey =
  (typeof TemplateCategories)[keyof typeof TemplateCategories];

// A labeled section of templates (e.g. "Global Hipaa Coversheet")
interface TemplateSection {
  label: string;
  icon: React.ReactNode;
  templates: TemplateDisplayItem[];
}

function sortTemplates(templates: TemplateDisplayItem[]) {
  templates.sort((a, b) => {
    if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
    return (a.display_name || a.name).localeCompare(b.display_name || b.name);
  });
}

const CATEGORY_DISPLAY: Record<string, string> = {
  HIPAA_COVERSHEET: 'HIPAA Coversheet',
  HIPAA_AUTHORIZATION: 'HIPAA Authorization',
  RETAINER: 'Retainer',
  OTHER: 'Other',
};

/**
 * Template list component
 * Shows all available templates (firm + global) organized by source then category
 */
export const TemplateSearchList: React.FC<TemplateSearchListProps> = ({
  requestInfo,
  onSelectTemplate,
  isLoading: externalLoading = false,
  includeCategories,
  allowedTemplateTypes,
  enablePreview = false,
  autoConfirmOnSelect = false,
  confirmButtonLabel = 'Add Template',
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(
    null,
  );

  // Get firm ID from matter details
  const { data: matterDetails } = useGetMatterDetailsQuery(
    requestInfo.matterId,
    { skip: !requestInfo.matterId },
  );
  const firmId = matterDetails?.firm?.id;

  // Fetch firm templates
  const { data: firmTemplatesData, isLoading: firmLoading } =
    useTemplateListQuery(
      { firmId: firmId || '', scope: 'FIRM', status: 'ACTIVE' },
      { skip: !firmId },
    );

  // Fetch global templates
  const { data: globalData, isLoading: globalLoading } =
    adminTemplatesApi.useAdminTemplatesListQuery(
      {
        isGlobal: true,
        limit: 500,
        includeDeleted: false,
        status: 'ACTIVE',
      },
      {},
    );

  // Build ordered sections: Global categories first, then Firm categories
  const sections = useMemo(() => {
    const firmTemplates = firmTemplatesData?.results ?? [];
    const globalTemplates = globalData?.results ?? [];

    const hasCategoryFilter = includeCategories && includeCategories.length > 0;
    const hasTypeFilter =
      allowedTemplateTypes && allowedTemplateTypes.length > 0;

    // Helper to check if a template passes filters
    const passesFilters = (
      category: string,
      format: TemplateFormat,
    ): boolean => {
      if (
        hasCategoryFilter &&
        !includeCategories?.includes(category as TemplateCategoryType)
      ) {
        return false;
      }
      if (hasTypeFilter && !allowedTemplateTypes?.includes(format)) {
        return false;
      }
      // Filter out PDF for HIPAA_AUTHORIZATION unless explicitly allowed
      if (
        category === 'HIPAA_AUTHORIZATION' &&
        format === 'PDF' &&
        !hasTypeFilter
      ) {
        return false;
      }
      return true;
    };

    // Normalize firm templates
    const firmItems: TemplateDisplayItem[] = firmTemplates
      .filter((t: ReadTemplateSummary) => passesFilters(t.category, t.format))
      .map((t: ReadTemplateSummary) => ({
        id: t.id as string,
        name: t.name,
        display_name: t.display_name || t.name,
        description: null,
        category: t.category,
        is_default: t.is_default,
        is_firm_template: true,
        format: t.format,
      }));

    // Normalize global templates
    const globalItems: TemplateDisplayItem[] = globalTemplates
      .filter((t) => passesFilters(t.category, t.format))
      .map((t) => ({
        id: t.id,
        name: t.display_name || t.name || 'Untitled',
        display_name: t.display_name ?? null,
        description: t.description,
        category: t.category,
        is_default: t.is_default,
        is_firm_template: false,
        format: t.format,
      }));

    // Group by category
    const categoryOrder: TemplateCategoryKey[] = [
      TemplateCategories.COVERSHEET,
      TemplateCategories.AUTHORIZATION,
      TemplateCategories.RETAINER,
      TemplateCategories.OTHER,
    ];

    // Global sections first, then firm sections
    const globalSections = categoryOrder
      .map((cat) => {
        const templates = globalItems.filter((t) => t.category === cat);
        sortTemplates(templates);
        return {
          label: `Global ${CATEGORY_DISPLAY[cat] ?? cat}`,
          icon: getCategoryIcon(cat),
          templates,
        };
      })
      .filter((s) => s.templates.length > 0);

    const firmSections = categoryOrder
      .map((cat) => {
        const templates = firmItems.filter((t) => t.category === cat);
        sortTemplates(templates);
        return {
          label: CATEGORY_DISPLAY[cat] ?? cat,
          icon: getCategoryIcon(cat),
          templates,
        };
      })
      .filter((s) => s.templates.length > 0);

    const result: TemplateSection[] = [...globalSections, ...firmSections];

    return result;
  }, [
    firmTemplatesData,
    globalData,
    includeCategories,
    allowedTemplateTypes,
  ]);

  const totalTemplates = sections.reduce(
    (sum, s) => sum + s.templates.length,
    0,
  );

  const isLoading = firmLoading || globalLoading || externalLoading;

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (autoConfirmOnSelect) {
      onSelectTemplate(templateId);
    }
  };

  const handleConfirm = () => {
    if (selectedTemplateId) {
      onSelectTemplate(selectedTemplateId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 mx-auto" />
          <Text className="mt-3 text-sm text-gray-600">
            Loading templates...
          </Text>
        </div>
      </div>
    );
  }

  if (totalTemplates === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <DocumentTextIcon className="h-12 w-12 text-gray-300 mb-4" />
        <Text className="text-gray-500 font-medium">
          No templates available
        </Text>
        <Text className="text-sm text-gray-500 mt-2">
          No document templates have been configured
        </Text>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Template list */}
      <div className="max-h-[500px] overflow-y-auto space-y-5 pr-1">
        {sections.map((section) => (
          <div key={section.label}>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              {section.icon}
              {section.label}
            </h4>
            <div className="space-y-2 ml-6">
              {section.templates.map((template) => (
                <TemplateItem
                  key={template.id}
                  template={template}
                  isSelected={selectedTemplateId === template.id}
                  onSelect={() => handleTemplateSelect(template.id)}
                  enablePreview={enablePreview}
                  onPreview={() => setPreviewTemplateId(template.id)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Action button */}
      {!autoConfirmOnSelect && selectedTemplateId && (
        <div className="sticky bottom-0 pt-4 border-t border-gray-200 bg-white">
          <Button
            onClick={handleConfirm}
            color="indigo"
            className="w-full shadow-sm"
          >
            {confirmButtonLabel}
          </Button>
        </div>
      )}
      {enablePreview && previewTemplateId && (
        <Dialog
          onClose={() => setPreviewTemplateId(null)}
          open
          zIndex={Z_INDEX_MODAL_DEEPLY_NESTED}
          nested
        >
          <DialogTitle>Template Preview</DialogTitle>
          <div className="mt-4 h-[70vh] overflow-hidden">
            <TemplatePreview
              templateId={previewTemplateId}
              matterId={requestInfo.matterId}
              overrides={{}}
            />
          </div>
        </Dialog>
      )}
    </div>
  );
};

/**
 * Get icon for template category
 */
const getCategoryIcon = (category: TemplateCategoryKey) => {
  switch (category) {
    case TemplateCategories.AUTHORIZATION:
      return <ShieldCheckIcon className="h-4 w-4 text-gray-500" />;
    case TemplateCategories.COVERSHEET:
      return <DocumentTextIcon className="h-4 w-4 text-gray-500" />;
    case TemplateCategories.RETAINER:
      return <DocumentTextIcon className="h-4 w-4 text-gray-500" />;
    default:
      return <DocumentTextIcon className="h-4 w-4 text-gray-500" />;
  }
};

/**
 * Template item component
 */
interface TemplateItemProps {
  template: TemplateDisplayItem;
  isSelected: boolean;
  onSelect: () => void;
  enablePreview?: boolean;
  onPreview?: () => void;
}

const TemplateItem: React.FC<TemplateItemProps> = ({
  template,
  isSelected,
  onSelect,
  enablePreview,
  onPreview,
}) => {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full px-3.5 py-3 border-2 rounded-xl text-left transition-all',
        'hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm',
        'active:scale-[0.99]',
        'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1',
        isSelected
          ? 'border-indigo-400 bg-indigo-50 hover:bg-indigo-50 shadow-sm'
          : 'border-gray-200 bg-white',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={cn(
                'font-semibold text-sm',
                isSelected ? 'text-indigo-900' : 'text-gray-900',
              )}
            >
              {template.display_name || template.name}
            </span>

            {/* Badges */}
            <div className="flex items-center gap-1.5">
              {template.format && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 uppercase">
                  {template.format}
                </span>
              )}
              {template.is_default && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                  Default
                </span>
              )}
            </div>
          </div>

          {template.description && (
            <p
              className={cn(
                'text-xs line-clamp-2 leading-relaxed',
                isSelected ? 'text-indigo-700' : 'text-gray-500',
              )}
            >
              {template.description}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-2">
          {enablePreview && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onPreview?.();
              }}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
            >
              Preview
            </button>
          )}
          {isSelected && (
            <div className="flex-shrink-0">
              <CheckIcon className="h-5 w-5 text-indigo-600 mt-0.5" />
            </div>
          )}
        </div>
      </div>
    </button>
  );
};
